import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
	requestAccountRecoverWithTypeME,
	requestAccountRecoverWithTypeRequest,
	showMessageRecoverFailed,
} from "../../../store/modules/recoverPassword/actions";

import { isValidCPFOrCNPJ, validateEmail } from "../../../utils/validator";

import { MAIL_TEMPLATE } from "../constants/mail-template";

import useDevice from "../../../hook/useDevice";

import { Button, Input, NavigationButton } from "../../../shared/components";
import { maskCpfCnpj, onlyNumbers, validatingMaxBrazilianPhoneLength } from "../../../utils";

import Recaptcha from "../../../components/Recaptcha";
import Phone from "../../../components/welcome/phone";

import {
	Container,
	Content,
	Title,
	FormGroupInput,
	Subtitle,
	HelpContainer,
	TitleHelp,
	FormSubmit,
	HeaderContent,
	MessageError,
	Col,
} from "../styles";
import { Label } from "../../../shared/components/Input/styles";
import config from "../../../config/ConfigApplication";

const schema = yup
	.object({
		email: yup.string().min(10).email("Revise o seu e-mail").required("Insira um e-mail válido"),
		document: yup.string().required("Insira um documento"),
		cellular: yup.string().min(5).required("Insira um telefone válido"),
	})
	.required();

export default function RecoverOption() {
	const lisButton = [
		{ label: "E-mail", value: "email" },
		{ label: "Celular", value: "cellular" },
	];

	const dispatch = useDispatch();
	const device = useDevice();

	const { loading } = useSelector((state) => state.recoverPassword);

	const [isValidCaptcha, setIsValidCaptcha] = useState(false);
	const [selectRecover, setSelectedRecover] = useState("email");
	const [tokenRecaptcha, setTokenRecaptcha] = useState(null);
	const [formStatus, setFormStatus] = useState({
		error: false,
		isEmpty: true,
	});

	const {
		register,
		setError,
		setValue,
		clearErrors,
		watch,
		resetField,
		reset,
		control,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
	});

	const { email, document, cellular } = watch();

	useEffect(() => {
		const { unsubscribe } = watch((value, { name }) => {
			_validationForm(value, name);

			const isEmpty = Object.values(value).some(
				(item) => item === null || item === undefined || item === ""
			);

			setFormStatus((prev) => ({
				...prev,
				isEmpty,
			}));
		});

		return () => unsubscribe();
	}, []);

	function sendPhone() {
		const recover = {
			email: () => _requestEmail(),
			cellular: () => _requestPhone(),
		};

		recover[selectRecover]();
	}

	function selectOptionRecover(method) {
		dispatch(showMessageRecoverFailed(false));
		setSelectedRecover(method);

		resetField();
		reset();

		setFormStatus({ error: false, isEmpty: true });
	}

	function handleClick() {
		if (device.isMobile) {
			return (window.location.href = MAIL_TEMPLATE);
		}
	}

	function _validationForm(value, name) {
		const action = {
			email: () => _validationEmail(value[name]),
			cellular: () => _validationPhone(value[name]),
			default: () => {},
		};

		(action[name] || action.default)();
	}

	const _validationDocument = useCallback(
		/** @param {string} doc */
		(doc) => {
			const { document, valid } = isValidCPFOrCNPJ(onlyNumbers(doc));

			if (valid || !doc.length) {
				clearErrors("document");
			} else {
				setError("document", {
					type: "manual",
					message: `Revise o ${document} digitado`,
				});
			}

			setFormStatus({
				error: !valid,
				isEmpty: !doc.length,
			});
		},
		[]
	);

	const _validationEmail = useCallback(
		/** @param {string} email */
		(email) => {
			const hasError = !validateEmail(email);

			if (hasError) {
				setError("email", { message: "Revise o seu e-mail" });
			} else {
				clearErrors("email");
			}

			setFormStatus({
				error: !isValid,
				isEmpty: !email.length,
			});
		},
		[]
	);

	function _handleDocument(documentData) {
		const value = maskCpfCnpj(documentData);

		_validationDocument(documentData);
		setValue("document", value);
	}

	const _validationPhone = useCallback(
		/** @param {string} phone */
		(phone) => {
			const hasErrorMessage = validatingMaxBrazilianPhoneLength(onlyNumbers(phone));
			const hasDigits = phone.length > 5;

			if (hasDigits && hasErrorMessage.length) {
				setError("cellular", { message: hasErrorMessage });
			} else {
				clearErrors("cellular");
			}

			setFormStatus({
				error: Boolean(hasErrorMessage.length),
				isEmpty: !hasDigits,
			});
		},
		[]
	);

	function _requestEmail() {
		dispatch(
			requestAccountRecoverWithTypeME(
				{
					baseInfoType: "email",
					baseInfoVl: email,
					operatingSystem: "WEB",
					recoverChannel: "email",
				},
				tokenRecaptcha
			)
		);
	}

	function _requestPhone() {
		const doc = onlyNumbers(document);
		const { document: documentType } = isValidCPFOrCNPJ(doc);

		dispatch(
			requestAccountRecoverWithTypeRequest({
				documentType: documentType.toUpperCase(),
				recoveryInfo: cellular,
				recoveryInfoType: "whatsapp",
				vlDocument: doc,
				operatingSystem: "WEB",
				header: tokenRecaptcha,
			})
		);
	}

	return (
		<Container id="recover">
			<Content>
				<HeaderContent>
					<Title>Recuperar senha</Title>
					<Subtitle>
						Vamos enviar um código por e-mail ou telefone cadastrado.
					</Subtitle>
				</HeaderContent>

				<NavigationButton
					list={lisButton}
					onChange={selectOptionRecover}
					active={selectRecover}
				/>

				<FormGroupInput>
					{selectRecover === "email" ? (
						<Input
							label="E-mail"
							placeholder="nome@exemplo.com"
							fieldRegister="email"
							register={register}
							required={true}
							autoFocus={true}
							errorMessage={errors.email}
						/>
					) : (
						<Col style={{ gap: "2rem" }}>
							<Input
								label="CPF ou CNPJ"
								placeholder="Digite seu CPF ou CNPJ"
								fieldRegister="document"
								register={register}
								required={true}
								autoFocus={true}
								errorMessage={errors.document}
								maxLength={18}
								onChangeCapture={({
									currentTarget: { value },
								}) => _handleDocument(value)}
							/>
							<Controller
								name="cellular"
								control={control}
								render={({
									field: { value },
									formState: { errors },
								}) => (
									<div>
										<Label required>
											Número de
											telefone
										</Label>
										<Phone
											numberPhone={
												value
											}
											onChange={(
												param
											) => {
												setValue(
													"cellular",
													String(
														param
													).replace(
														/\D/g,
														""
													)
												);

												dispatch(
													showMessageRecoverFailed(
														false
													)
												);

												if (
													param.length <=
													2
												) {
													setFormStatus(
														{
															error: false,
															isEmpty: true,
														}
													);
												}
											}}
											error={
												errors.cellular &&
												errors
													.cellular
													.message
													.length
											}
										/>
										<MessageError>
											{errors.cellular
												? errors
														.cellular
														.message
												: null}
										</MessageError>
									</div>
								)}
							/>
						</Col>
					)}
				</FormGroupInput>

				<Recaptcha
					isValid={(param) => setIsValidCaptcha(param)}
					token={setTokenRecaptcha}
				/>

				<FormSubmit>
					<Button
						title="Enviar código"
						onClick={() => sendPhone()}
						disabled={
							formStatus.error ||
							formStatus.isEmpty ||
							Object.keys(errors).length ||
							!isValidCaptcha
						}
						loading={loading}
					></Button>
				</FormSubmit>

				<HelpContainer>
					<TitleHelp visibility={config.enableSupport ? "block" : "none"}>
						Mudou seu telefone e seu e-mail?
					</TitleHelp>

					{device.isMobile ? (
						<Subtitle>
							<a onClick={handleClick}>Clique aqui</a> que nossa
							equipe vai te ajudar a restaurar o acesso à sua
							conta!
						</Subtitle>
					) : (
						<Subtitle
							visibility={
								config.enableSupport ? "block" : "none"
							}
						>
							Clique no botão “Ajuda” no canto inferior direito
							da tela e preencha os campos para nossa equipe te
							ajudar a restaurar o acesso a sua conta!
						</Subtitle>
					)}
				</HelpContainer>
			</Content>
		</Container>
	);
}
